import { useQueries } from "@tanstack/react-query";
import apiSubscription from "../../services/subscription";
import { mockPolicies, mockDelay } from "../../views/SubscriptionProjectV2/lib/mockdata";

/**
 * Fetches all 3 policy documents (Terms / Privacy / Refund) in parallel.
 *
 * Why one hook instead of three:
 *  PaymentStep needs all 3 modals available at once. Calling 3 separate hooks
 *  is fine but produces 3 isLoading flags to juggle. This consolidates them
 *  into one return shape:
 *    { terms: { html, isLoading }, privacy: { html, isLoading }, refund: { ... } }
 *  matching what PaymentStep expects.
 */
const USE_MOCK_DATA = false;

const fetchPolicy = async (kind, id_project) => {
  if (USE_MOCK_DATA) {
    await mockDelay(200);
    const rows = mockPolicies[kind] ?? [];
    return rows[0]?.description ?? "";
  }

  const fetcher = {
    terms: apiSubscription.getTermsCondition,
    privacy: apiSubscription.getPrivacyPolicy,
    refund: apiSubscription.getRefundablePolicy,
  }[kind];

  const res = await fetcher.call(apiSubscription, { id_project });
  // Backend returns array of rows; HTML lives in [0].description
  return res?.dataRes?.[0]?.description ?? res?.[0]?.description ?? "";
};

export function useTermsPrivacyRefund({ id_project } = {}) {
  const results = useQueries({
    queries: ["terms", "privacy", "refund"].map((kind) => ({
      queryKey: ["subscription", kind, id_project],
      queryFn: () => fetchPolicy(kind, id_project),
      enabled: !!id_project,
      staleTime: 1000 * 60 * 30, // policies rarely change
    })),
  });

  const [termsRes, privacyRes, refundRes] = results;

  return {
    terms: { html: termsRes.data, isLoading: termsRes.isLoading },
    privacy: { html: privacyRes.data, isLoading: privacyRes.isLoading },
    refund: { html: refundRes.data, isLoading: refundRes.isLoading },
  };
}